// src/components/SalesDashboard/SalesOrderCreate.js
import React, { useState, useEffect } from 'react';
import {
  ChevronLeft, Trash2, Search, Loader, CheckCircle, AlertTriangle,
} from 'lucide-react';
import api from '../api';
import { useMasterData } from '../../utils/useMasterData';

const S = `
  .soc-wrap { max-width: 900px; margin: 0 auto; font-family: 'Inter', sans-serif; color: #000; }
  .soc-back { display: flex; align-items: center; gap: 6px; background: none; border: none; color: #6366f1; font-weight: 700; font-size: 13px; cursor: pointer; margin-bottom: 20px; padding: 0; }

  .soc-card { background: white; border-radius: 16px; border: 1px solid #e2e8f0; padding: 24px; margin-bottom: 20px; }
  .soc-card-title { font-size: 15px; font-weight: 800; color: #000; margin: 0 0 18px; }

  .soc-field { margin-bottom: 16px; }
  .soc-label { display: block; font-size: 12px; font-weight: 600; color: #1a1a1a; margin-bottom: 6px; }
  .soc-input, .soc-select, .soc-textarea {
    width: 100%; padding: 10px 14px; border: 1.5px solid #e2e8f0; border-radius: 10px;
    font-size: 14px; outline: none; box-sizing: border-box; color: #1e293b; background: white;
  }
  .soc-input:focus, .soc-select:focus, .soc-textarea:focus { border-color: #6366f1; }
  .soc-textarea { resize: vertical; }

  .soc-row2 { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
  .soc-row3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 16px; }
  @media(max-width:600px) { .soc-row2, .soc-row3 { grid-template-columns: 1fr; } }

  .soc-search-box { position: relative; }
  .soc-search-icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); pointer-events: none; }
  .soc-search-box input { padding-left: 36px; }
  .soc-dropdown { position: absolute; z-index: 20; background: white; border: 1.5px solid #e2e8f0; border-radius: 10px; width: 100%; max-height: 220px; overflow-y: auto; box-shadow: 0 8px 24px rgba(0,0,0,.1); margin-top: 4px; }
  .soc-dropdown-item { padding: 10px 14px; cursor: pointer; font-size: 13px; border-bottom: 1px solid #f1f5f9; }
  .soc-dropdown-item:last-child { border-bottom: none; }
  .soc-dropdown-item:hover { background: #f1f5f9; }
  .soc-selected-chip { display: inline-flex; align-items: center; gap: 6px; background: #eef2ff; color: #4f46e5; border-radius: 8px; padding: 6px 12px; font-size: 13px; font-weight: 600; margin-bottom: 8px; }

  .soc-items-table { width: 100%; border-collapse: collapse; font-size: 13px; }
  .soc-items-table th { padding: 9px 12px; text-align: left; font-size: 11px; font-weight: 700; color: #1a1a1a; text-transform: uppercase; border-bottom: 2px solid #e2e8f0; }
  .soc-items-table td { padding: 10px 12px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; color: #000; }
  .soc-items-table tbody tr:last-child td { border-bottom: none; }
  .soc-items-table input { width: 100%; padding: 6px 10px; border: 1.5px solid #e2e8f0; border-radius: 8px; font-size: 13px; outline: none; box-sizing: border-box; }
  .soc-items-table input:focus { border-color: #6366f1; }

  .soc-add-row { display: flex; align-items: center; gap: 8px; margin-top: 12px; }
  .soc-add-search { flex: 1; position: relative; }

  .soc-totals { background: linear-gradient(135deg,#1e1b4b,#312e81); border-radius: 14px; padding: 18px 22px; color: white; margin-bottom: 20px; }
  .soc-totals-row { display: flex; justify-content: space-between; font-size: 14px; padding: 4px 0; }
  .soc-totals-grand { font-size: 20px; font-weight: 800; }

  .soc-btn {
    display: inline-flex; align-items: center; gap: 7px; padding: 11px 22px;
    border-radius: 10px; font-size: 14px; font-weight: 700; cursor: pointer;
    border: none; transition: opacity .15s;
  }
  .soc-btn:hover { opacity: .88; }
  .soc-btn:disabled { opacity: .5; cursor: not-allowed; }
  .soc-btn-primary { background: linear-gradient(135deg,#1e1b4b,#312e81); color: white; }
  .soc-btn-outline { background: white; color: #1a1a1a; border: 1.5px solid #e2e8f0; }
  .soc-btn-ghost { background: none; border: none; color: #ef4444; cursor: pointer; padding: 4px; }
  .soc-btn-ghost:hover { opacity: .7; }
  .soc-btn-add { background: #eef2ff; color: #6366f1; border: 1.5px solid #c7d2fe; border-radius: 8px; padding: 7px 14px; font-size: 12px; font-weight: 700; cursor: pointer; display: inline-flex; align-items: center; gap: 5px; }
`;

export default function SalesOrderCreate({ onBack, onCreated }) {
  const { getOptions } = useMasterData('PRIORITY');
  const priorityOptions = getOptions('PRIORITY');
  // Customer
  const [customers, setCustomers] = useState([]);
  const [custSearch, setCustSearch] = useState('');
  const [custOpen, setCustOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Designs
  const [designs, setDesigns] = useState([]);
  const [designSearch, setDesignSearch] = useState('');
  const [designOpen, setDesignOpen] = useState(false);

  // Items
  const [items, setItems] = useState([]);

  // Order details
  const [deliveryDate, setDeliveryDate] = useState('');
  const [advance, setAdvance] = useState('');
  const [priority, setPriority] = useState('3');
  const [instructions, setInstructions] = useState('');

  // UI
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/customers', { params: { per_page: 200 } })
      .then(r => setCustomers(r.data.data || []))
      .catch(() => {});
    api.get('/job-cards/designs')
      .then(r => setDesigns(r.data.data || []))
      .catch(() => {});
  }, []);

  const filteredCustomers = customers.filter(c =>
    c.name?.toLowerCase().includes(custSearch.toLowerCase()) ||
    c.contact_phone?.includes(custSearch)
  );

  const filteredDesigns = designs.filter(d =>
    d.design_code?.toLowerCase().includes(designSearch.toLowerCase()) ||
    d.design_name?.toLowerCase().includes(designSearch.toLowerCase())
  );

  const addItem = (design) => {
    setItems(prev => [...prev, {
      design_id: design.design_id,
      design_code: design.design_code,
      design_name: design.design_name,
      quantity: 1,
      unit_price: '',
      metal_type: '22K Gold',
      special_requirements: '',
    }]);
    setDesignSearch('');
    setDesignOpen(false);
  };

  const updateItem = (idx, field, val) => {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, [field]: val } : it));
  };

  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

  const subtotal = items.reduce((s, it) => s + (parseFloat(it.unit_price) || 0) * (parseInt(it.quantity) || 0), 0);

  const handleSubmit = async () => {
    if (!selectedCustomer) { setError('Please select a customer.'); return; }
    if (items.length === 0) { setError('Please add at least one design item.'); return; }
    if (items.some(it => !it.unit_price || it.unit_price <= 0)) {
      setError('All items must have a unit price greater than 0.'); return;
    }

    setSubmitting(true);
    setError('');
    try {
      const res = await api.post('/sales/orders', {
        customer_id: selectedCustomer.id,
        priority: parseInt(priority),
        expected_delivery_date: deliveryDate || null,
        advance_received: parseFloat(advance) || 0,
        special_instructions: instructions,
        items: items.map(it => ({
          design_id: it.design_id,
          quantity: parseInt(it.quantity),
          unit_price: parseFloat(it.unit_price),
          metal_type: it.metal_type,
          special_requirements: it.special_requirements,
        })),
      });
      onCreated(res.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to create order.');
    }
    setSubmitting(false);
  };

  return (
    <>
      <style>{S}</style>
      <div className="soc-wrap">
        <button className="soc-back" onClick={onBack}>
          <ChevronLeft size={16} /> Back to Orders
        </button>

        <h2 style={{ fontSize: 22, fontWeight: 800, color: '#000', margin: '0 0 24px' }}>
          New Direct Sales Order
        </h2>

        {error && (
          <div style={{ background: '#fef2f2', border: '1.5px solid #fca5a5', borderRadius: 10, padding: '12px 16px', color: '#dc2626', fontSize: 13, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertTriangle size={16} /> {error}
          </div>
        )}

        {/* Customer */}
        <div className="soc-card">
          <div className="soc-card-title">Customer</div>
          {selectedCustomer ? (
            <div className="soc-selected-chip">
              {selectedCustomer.name}
              {selectedCustomer.contact_phone && <span style={{ color: '#818cf8', fontSize: 12 }}> · {selectedCustomer.contact_phone}</span>}
              <button className="soc-btn-ghost" onClick={() => setSelectedCustomer(null)} style={{ marginLeft: 4 }}>×</button>
            </div>
          ) : (
            <div className="soc-search-box">
              <Search size={14} color="#94a3b8" className="soc-search-icon" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
              <input
                className="soc-input"
                style={{ paddingLeft: 36 }}
                placeholder="Search customer by name or phone…"
                value={custSearch}
                onChange={e => { setCustSearch(e.target.value); setCustOpen(true); }}
                onFocus={() => setCustOpen(true)}
                onBlur={() => setTimeout(() => setCustOpen(false), 150)}
              />
              {custOpen && filteredCustomers.length > 0 && (
                <div className="soc-dropdown">
                  {filteredCustomers.slice(0, 10).map(c => (
                    <div key={c.id} className="soc-dropdown-item" onMouseDown={() => { setSelectedCustomer(c); setCustOpen(false); setCustSearch(''); }}>
                      <strong>{c.name}</strong>
                      {c.contact_phone && <span style={{ color: '#1a1a1a', fontSize: 12 }}> · {c.contact_phone}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Order Details */}
        <div className="soc-card">
          <div className="soc-card-title">Order Details</div>
          <div className="soc-row3">
            <div className="soc-field">
              <label className="soc-label">Priority</label>
              <select className="soc-select" value={priority} onChange={e => setPriority(e.target.value)}>
                {priorityOptions.map((option) => (
                  <option key={option.value} value={String(option.value).split('-')[0]}>{option.label}</option>
                ))}
              </select>
            </div>
            <div className="soc-field">
              <label className="soc-label">Expected Delivery Date</label>
              <input
                type="date"
                className="soc-input"
                value={deliveryDate}
                onChange={e => setDeliveryDate(e.target.value)}
              />
            </div>
            <div className="soc-field">
              <label className="soc-label">Advance Received (₹)</label>
              <input
                type="number"
                className="soc-input"
                placeholder="0.00"
                value={advance}
                onChange={e => setAdvance(e.target.value)}
                min="0"
              />
            </div>
          </div>
          <div className="soc-field">
            <label className="soc-label">Special Instructions</label>
            <textarea
              className="soc-textarea"
              rows={2}
              placeholder="Any special instructions for production…"
              value={instructions}
              onChange={e => setInstructions(e.target.value)}
            />
          </div>
        </div>

        {/* Design Items */}
        <div className="soc-card">
          <div className="soc-card-title">Design Items</div>

          {items.length > 0 && (
            <div style={{ overflowX: 'auto', marginBottom: 16 }}>
              <table className="soc-items-table">
                <thead>
                  <tr>
                    <th>Design</th>
                    <th>Metal Type</th>
                    <th>Qty</th>
                    <th>Unit Price (₹)</th>
                    <th>Total (₹)</th>
                    <th>Special Req.</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, idx) => (
                    <tr key={idx}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{it.design_code}</div>
                        <div style={{ fontSize: 11, color: '#1a1a1a' }}>{it.design_name}</div>
                      </td>
                      <td>
                        <input
                          value={it.metal_type}
                          onChange={e => updateItem(idx, 'metal_type', e.target.value)}
                          placeholder="22K Gold"
                          style={{ width: 100 }}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={it.quantity}
                          onChange={e => updateItem(idx, 'quantity', e.target.value)}
                          min="1"
                          style={{ width: 60 }}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={it.unit_price}
                          onChange={e => updateItem(idx, 'unit_price', e.target.value)}
                          placeholder="0"
                          min="0"
                          style={{ width: 100 }}
                        />
                      </td>
                      <td style={{ fontWeight: 700 }}>
                        ₹{((parseFloat(it.unit_price) || 0) * (parseInt(it.quantity) || 0)).toLocaleString('en-IN')}
                      </td>
                      <td>
                        <input
                          value={it.special_requirements}
                          onChange={e => updateItem(idx, 'special_requirements', e.target.value)}
                          placeholder="Optional…"
                          style={{ width: 140 }}
                        />
                      </td>
                      <td>
                        <button className="soc-btn-ghost" onClick={() => removeItem(idx)}>
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Add design row */}
          <div className="soc-add-row">
            <div className="soc-add-search" style={{ position: 'relative', flex: 1 }}>
              <Search size={14} color="#94a3b8" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
              <input
                className="soc-input"
                style={{ paddingLeft: 36 }}
                placeholder="Search design to add…"
                value={designSearch}
                onChange={e => { setDesignSearch(e.target.value); setDesignOpen(true); }}
                onFocus={() => setDesignOpen(true)}
                onBlur={() => setTimeout(() => setDesignOpen(false), 150)}
              />
              {designOpen && filteredDesigns.length > 0 && (
                <div className="soc-dropdown">
                  {filteredDesigns.slice(0, 10).map(d => (
                    <div
                      key={d.design_id}
                      className="soc-dropdown-item"
                      onMouseDown={() => addItem(d)}
                    >
                      <strong>{d.design_code}</strong>
                      {d.design_name && <span style={{ color: '#1a1a1a', fontSize: 12 }}> — {d.design_name}</span>}
                      {d.actual_weight && <span style={{ color: '#94a3b8', fontSize: 11, marginLeft: 8 }}>{d.actual_weight}g</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Totals */}
        {items.length > 0 && (
          <div className="soc-totals">
            <div className="soc-totals-row">
              <span>Subtotal</span>
              <span>₹{subtotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            </div>
            <div className="soc-totals-row">
              <span>Advance Received</span>
              <span>₹{(parseFloat(advance) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            </div>
            <hr style={{ border: 'none', borderTop: '1px solid rgba(255,255,255,.2)', margin: '8px 0' }} />
            <div className="soc-totals-row soc-totals-grand">
              <span>Balance Due</span>
              <span>₹{Math.max(0, subtotal - (parseFloat(advance) || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            </div>
          </div>
        )}

        {/* Footer actions */}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button className="soc-btn soc-btn-outline" onClick={onBack} disabled={submitting}>
            Cancel
          </button>
          <button className="soc-btn soc-btn-primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? <Loader size={14} /> : <CheckCircle size={14} />}
            {submitting ? 'Creating…' : 'Confirm Order'}
          </button>
        </div>
      </div>
    </>
  );
}
